import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ConfirmDialog, ErrorState, Icon, IconButton, InfoRow, MenuDropdown, PageHeader, PageLoader, Tabs, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { LedgerStatement } from '../../../components/common/LedgerStatement/LedgerStatement';
import { PartyCard } from '../../../components/common/PartyCard/PartyCard';
import { KeyValues, TwoColumn } from '../../../components/common/Parts/Parts';
import { RelatedList } from '../../../components/common/RelatedList/RelatedList';
import { useAction } from '../../../hooks/useAction';
import { useCustomer } from '../hooks/useCustomers';
import { customerService } from '../services/customerService';
import { cn } from '../../../utils/cn';
import { INVOICE_STATUS, JOB_STATUS, QUOTATION_STATUS } from '../../../constants/status';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import type { Address } from '../../../types/common';
import type { Customer } from '../types/customerTypes';

const addressText = (a?: Address) => (a ? [a.line, a.city, a.pincode].filter(Boolean).join(', ') : '');

function BalanceCard({ c }: { c: Customer }) {
  const bal = c.balance ?? 0;
  const [label, tone] = bal > 0 ? ['To collect', 'text-brand-700'] : bal < 0 ? ['Advance with you', 'text-emerald-700'] : ['All settled', 'text-navy-900'];
  return (
    <Card>
      <CardHeader title="Summary" />
      <div className="p-5 pt-4">
        <KeyValues rows={[['Total invoiced', money(c.totalBilled)], ['Total paid', money(c.totalPaid)]]} />
        <div className="mt-4 flex items-center justify-between gap-3 rounded-lg bg-brand-50/70 px-4 py-3">
          <span className="text-sm font-medium text-navy-900">{label}</span>
          <span className={cn('tabular text-xl font-semibold', tone)}>{money(Math.abs(bal))}</span>
        </div>
      </div>
    </Card>
  );
}

export default function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [tab, setTab] = useState('details');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const { data: c, error, reload } = useCustomer(id);
  useCrumb(`/customers/${id}`, c?.name);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!c || !id) return <PageLoader />;

  const remove = async () => {
    if (await run('delete', () => customerService.remove(id), `${c.name} deleted`)) navigate('/customers', { replace: true });
    setConfirmDelete(false);
  };

  return (
    <>
      <PageHeader
        title={c.name}
        badge={<Badge tone={c.isActive ? 'green' : 'grey'} dot>{c.isActive ? 'Active' : 'Inactive'}</Badge>}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span className="font-mono">{c.customerCode}</span>
            <span className="inline-flex items-center gap-1.5"><Icon name="user" size={15} />{c.customerType === 'business' ? 'Business' : 'Individual'}</span>
            {c.phone && <span className="inline-flex items-center gap-1.5"><Icon name="phone" size={15} />{c.phone}</span>}
            {c.billingAddress?.city && <span className="inline-flex items-center gap-1.5"><Icon name="mapPin" size={15} />{c.billingAddress.city}</span>}
          </span>
        }
        actions={
          <>
            <Button variant="dangerText" onClick={() => setConfirmDelete(true)}>Delete</Button>
            <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/customers/${id}/edit`)}>Edit</Button>
            <MenuDropdown
              trigger={<IconButton icon="more" label="More actions" variant="outline" />}
              items={[
                { label: 'New service job', icon: 'wrench', onClick: () => navigate(`/jobs/new?customerId=${id}`) },
                { label: 'New invoice', icon: 'receipt', onClick: () => navigate(`/invoices/new?customerId=${id}`) },
                { label: 'Payment received', icon: 'moneyIn', onClick: () => navigate(`/transactions/new?category=invoice_payment&customerId=${id}`) },
                { label: 'Advance received', icon: 'wallet', onClick: () => navigate(`/transactions/new?category=advance_customer&customerId=${id}`) },
              ]}
            />
            <Button leftIcon="plus" onClick={() => navigate(`/quotations/new?customerId=${id}`)}>New quotation</Button>
          </>
        }
      />

      <TwoColumn
        asideFirst
        main={
          <>
            <Tabs
              label="Customer sections"
              value={tab}
              onChange={setTab}
              tabs={[
                { value: 'details', label: 'Details' },
                { value: 'quotations', label: 'Quotations' },
                { value: 'jobs', label: 'Jobs' },
                { value: 'invoices', label: 'Invoices' },
                { value: 'statement', label: 'Statement' },
              ]}
            />
            {tab === 'details' && (
              <Card className="p-5">
                <dl className="grid gap-5 sm:grid-cols-2">
                  <InfoRow label="Mobile">{c.phone}</InfoRow>
                  <InfoRow label="Other phone">{c.altPhone}</InfoRow>
                  <InfoRow label="Email">{c.email}</InfoRow>
                  {c.customerType === 'business' && <InfoRow label="GSTIN">{c.gstin}</InfoRow>}
                  <div className="sm:col-span-2"><InfoRow label="Address">{addressText(c.billingAddress)}</InfoRow></div>
                  {c.notes && <div className="sm:col-span-2"><InfoRow label="Notes">{c.notes}</InfoRow></div>}
                </dl>
              </Card>
            )}
            {tab === 'quotations' && (
              <RelatedList
                path="/quotations"
                query={{ customerId: id }}
                statusMap={QUOTATION_STATUS}
                icon="quote"
                emptyTitle="No quotations yet"
                action={<Button leftIcon="plus" onClick={() => navigate(`/quotations/new?customerId=${id}`)}>New quotation</Button>}
                row={(q) => ({ to: `/quotations/${q._id}`, title: `${q.quotationNo}${q.title ? ` · ${q.title}` : ''}`, sub: formatDate(q.date), status: q.status, amount: money(q.grandTotal) })}
              />
            )}
            {tab === 'jobs' && (
              <RelatedList
                path="/services"
                query={{ customerId: id }}
                statusMap={JOB_STATUS}
                icon="wrench"
                emptyTitle="No jobs yet"
                row={(s) => ({ to: `/jobs/${s._id}`, title: `${s.serviceNo}${s.title ? ` · ${s.title}` : ''}`, sub: s.scheduledDate ? `Visit ${formatDate(s.scheduledDate)}` : undefined, status: s.status })}
              />
            )}
            {tab === 'invoices' && (
              <RelatedList
                path="/invoices"
                query={{ customerId: id }}
                statusMap={INVOICE_STATUS}
                icon="receipt"
                emptyTitle="No invoices yet"
                row={(i) => ({
                  to: `/invoices/${i._id}`,
                  title: i.invoiceNo ?? 'Draft invoice',
                  sub: `${formatDate(i.date)}${i.status === 'final' && i.balanceAmount > 0 ? ` · ${money(i.balanceAmount)} due` : ''}`,
                  status: i.status,
                  amount: money(i.grandTotal),
                })}
              />
            )}
            {tab === 'statement' && <LedgerStatement path={customerService.ledgerPath(id)} emptyMessage="Invoices and payments for this customer will appear here." />}
          </>
        }
        aside={
          <>
            <BalanceCard c={c} />
            <PartyCard title="Contact" party={c} />
          </>
        }
      />

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={remove}
        loading={busy === 'delete'}
        danger
        title="Delete this customer?"
        message="They will be removed from lists. Old invoices and payments stay saved. Customers with a pending balance can't be deleted."
        confirmLabel="Delete customer"
      />
    </>
  );
}
